<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '../stores/auth'
import authService from '../services/auth.service'

const { locale } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Get language from URL query param, localStorage, or default to 'pl'
const getInitialLanguage = (): string => {
  const urlLang = route.query.lang as string
  const supportedLangs = ['pl', 'en', 'fr', 'es', 'de', 'it', 'pt', 'cs', 'nl', 'sv', 'da', 'hu', 'ro', 'no', 'lt', 'hr', 'fi']

  if (urlLang && supportedLangs.includes(urlLang)) {
    return urlLang
  }

  return localStorage.getItem('language') || 'pl'
}

const selectedLanguage = ref(getInitialLanguage())
const dropdownOpen = ref(false)

// Set initial locale
onMounted(() => {
  // Save to localStorage if came from URL
  if (route.query.lang) {
    localStorage.setItem('language', selectedLanguage.value)
  }

  locale.value = selectedLanguage.value
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Handle language change
const changeLanguage = async (lang: string) => {
  selectedLanguage.value = lang
  locale.value = lang
  localStorage.setItem('language', lang)

  // Note: URL query param update removed - caused navigation issues on native platforms
  // Language is persisted via localStorage which is sufficient

  // If user is logged in, update language in backend
  if (authStore.isAuthenticated && authStore.currentUser?.uuid) {
    try {
      await authService.updateProfile(authStore.currentUser.uuid, {
        language: lang
      })

      // Update initData lang to keep in sync
      if (authStore.initData) {
        authStore.initData.lang = lang
        authService.saveInitData(authStore.initData)
      }
    } catch (err) {
      console.error('Failed to update language in backend:', err)
      // Continue anyway - language is changed in frontend
    }
  }

  dropdownOpen.value = false
}

// Toggle dropdown
const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.language-selector')) {
    dropdownOpen.value = false
  }
}

// Get flag and label for current language
const currentLanguage = ref({
  pl: { flag: '🇵🇱', label: 'Polski' },
  en: { flag: '🇬🇧', label: 'English' },
  fr: { flag: '🇫🇷', label: 'Français' },
  es: { flag: '🇪🇸', label: 'Español' },
  de: { flag: '🇩🇪', label: 'Deutsch' },
  it: { flag: '🇮🇹', label: 'Italiano' },
  pt: { flag: '🇵🇹', label: 'Português' },
  cs: { flag: '🇨🇿', label: 'Čeština' },
  nl: { flag: '🇳🇱', label: 'Nederlands' },
  sv: { flag: '🇸🇪', label: 'Svenska' },
  da: { flag: '🇩🇰', label: 'Dansk' },
  hu: { flag: '🇭🇺', label: 'Magyar' },
  ro: { flag: '🇷🇴', label: 'Română' },
  no: { flag: '🇳🇴', label: 'Norsk' },
  lt: { flag: '🇱🇹', label: 'Lietuvių' },
  hr: { flag: '🇭🇷', label: 'Hrvatski' },
  fi: { flag: '🇫🇮', label: 'Suomi' }
})
</script>

<template>
  <div class="relative language-selector">
    <button
      @click.stop="toggleDropdown"
      class="px-3 py-2 text-sm border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:border-blue-600 flex items-center gap-2"
    >
      <span>{{ currentLanguage[selectedLanguage].flag }} {{ currentLanguage[selectedLanguage].label }}</span>
      <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-show="dropdownOpen"
      class="absolute left-0 mt-2 w-40 bg-white border-2 border-gray-300 rounded shadow-md z-10"
    >
      <button
        @click="changeLanguage('pl')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'pl' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇵🇱 Polski
      </button>
      <button
        @click="changeLanguage('en')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'en' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇬🇧 English
      </button>
      <button
        @click="changeLanguage('fr')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'fr' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇫🇷 Français
      </button>
      <button
        @click="changeLanguage('es')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'es' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇪🇸 Español
      </button>
      <button
        @click="changeLanguage('de')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'de' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇩🇪 Deutsch
      </button>
      <button
        @click="changeLanguage('it')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'it' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇮🇹 Italiano
      </button>
      <button
        @click="changeLanguage('pt')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'pt' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇵🇹 Português
      </button>
      <button
        @click="changeLanguage('cs')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'cs' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇨🇿 Čeština
      </button>
      <button
        @click="changeLanguage('nl')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'nl' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇳🇱 Nederlands
      </button>
      <button
        @click="changeLanguage('sv')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'sv' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇸🇪 Svenska
      </button>
      <button
        @click="changeLanguage('da')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'da' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇩🇰 Dansk
      </button>
      <button
        @click="changeLanguage('hu')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'hu' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇭🇺 Magyar
      </button>
      <button
        @click="changeLanguage('ro')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'ro' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇷🇴 Română
      </button>
      <button
        @click="changeLanguage('no')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'no' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇳🇴 Norsk
      </button>
      <button
        @click="changeLanguage('lt')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'lt' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇱🇹 Lietuvių
      </button>
      <button
        @click="changeLanguage('hr')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'hr' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇭🇷 Hrvatski
      </button>
      <button
        @click="changeLanguage('fi')"
        :class="[
          'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
          selectedLanguage === 'fi' ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
        ]"
      >
        🇫🇮 Suomi
      </button>
    </div>
  </div>
</template>
